import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { adminDashboardApi, adminCentersApi } from '@api/client';
import type { Booking, Center, DashboardSummary } from '@api/types';
import { useAuth } from '@hooks/useAuth';
import { useActiveCenter, ALL_CENTERS } from '@hooks/useActiveCenter';
import { useNow } from '@hooks/useNow';
import * as Icons from '@components/Icons';
import { PageHead } from '@components/admin/PageHead';
import { BookingDetailDrawer, STATUS_MAP, initials, tone } from './BookingDetailDrawer';
import { Shortcuts, type Role } from './inicio/Shortcuts';

const MONTHS_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DAYS_ES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

function greeting(date: Date, name: string): string {
  const h = date.getHours();
  const slot = h < 6 ? 'Buenas noches' : h < 13 ? 'Buenos días' : h < 20 ? 'Buenas tardes' : 'Buenas noches';
  return `${slot}, ${name}`;
}

function fmtToday(date: Date): string {
  return `${DAYS_ES[date.getDay()] ?? ''} · ${String(date.getDate())} de ${MONTHS_ES[date.getMonth()] ?? ''}, ${String(date.getFullYear())}`;
}

function fmtClock(date: Date): string {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function fmtTime(iso: string): string {
  return iso.slice(11, 16);
}

function fmtDateShort(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  return `${String(d)} ${MONTHS_SHORT[m - 1] ?? ''}`;
}

function firstName(email: string): string {
  const local = email.split('@')[0] ?? '';
  const seg = local.split(/[._-]/)[0] ?? local;
  if (!seg) return 'hola';
  return seg.charAt(0).toUpperCase() + seg.slice(1).toLowerCase();
}

export function InicioPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openId, setOpenId] = useState<string | null>(null);

  // Rol guard — el componente asume que useAuth devuelve el rol; si no
  // hay user (estado raro), tratamos como staff para no leakear más datos
  // de lo necesario.
  const role: Role = useMemo<Role>(() => {
    const r = user?.role;
    if (r === 'superadmin' || r === 'center_admin' || r === 'staff') return r;
    return 'staff';
  }, [user]);
  const isSuper = role === 'superadmin';
  const isStaff = role === 'staff';
  const linkedStaffId = user?.linkedStaffId ?? null;

  // Filtro de centro compartido con las demás páginas vía `useActiveCenter`.
  // `'all'` → backend usa el allowed set del rol; un id concreto se manda
  // como parámetro al dashboard.
  const { activeCenterId, setActiveCenterId, supportsAll } = useActiveCenter();
  const centerId = activeCenterId === ALL_CENTERS ? '' : activeCenterId;
  const [staffOnly, setStaffOnly] = useState<boolean>(false);

  // Lista de centros disponibles para el dropdown. Superadmin carga todos
  // los centros activos del catálogo. Center_admin sintetiza la lista
  // desde `user.adminCenterIds` (no tiene permiso para listar centros).
  // Staff no ve dropdown.
  const { data: allCenters } = useQuery<Center[]>({
    queryKey: ['centers', 'all-active'],
    queryFn: async () => {
      const raw = await adminCentersApi.list();
      // adminCentersApi.list devuelve `unknown` (paginated o array según
      // endpoint). Aceptamos ambos shapes y filtramos al frontend.
      if (Array.isArray(raw)) return raw as Center[];
      const data = (raw as { data?: unknown }).data;
      return Array.isArray(data) ? (data as Center[]) : [];
    },
    enabled: isSuper,
    staleTime: 5 * 60_000,
  });
  const centerOptions: { id: string; name: string }[] = useMemo(() => {
    if (isStaff) return [];
    if (isSuper) {
      return (allCenters ?? []).filter((c) => c.isActive !== false).map((c) => ({ id: c.id, name: c.name }));
    }
    // center_admin: lista sintética
    const ids = user?.adminCenterIds ?? [];
    return ids.map((id) => ({ id, name: `Centro ${id.slice(-6)}` }));
  }, [isStaff, isSuper, allCenters, user]);
  const showCenterDropdown = (isSuper || (centerOptions.length > 1)) && !isStaff;

  const staffIdParam = isStaff
    ? undefined // staff: backend lo fuerza desde JWT, no manda
    : (staffOnly && linkedStaffId ? linkedStaffId : undefined);

  const { data, isLoading, error } = useQuery<DashboardSummary>({
    queryKey: ['dashboard', centerId || 'all', staffIdParam ?? 'all'],
    queryFn: () => adminDashboardApi.get({
      ...(centerId ? { centerId } : {}),
      ...(staffIdParam ? { staffId: staffIdParam } : {}),
    }),
    staleTime: 30_000,
  });

  const now = useNow(30_000);
  const userName = user?.email ? firstName(user.email) : 'hola';

  const allBookings: readonly Booking[] = useMemo(() => {
    if (!data) return [];
    return [...data.todayAgenda, ...data.pendingApprovalSample];
  }, [data]);
  const openBooking = openId ? allBookings.find((b) => b.id === openId) ?? null : null;

  // Reset toggle "Mis reservas" si pierdes el linkedStaffId (logout/relog
  // como cuenta sin staff).
  useEffect(() => {
    if (!linkedStaffId && staffOnly) setStaffOnly(false);
  }, [linkedStaffId, staffOnly]);

  if (isLoading) {
    return (
      <div>
        <PageHead title="Inicio" sub="Cargando..." />
        <div className="inicio-empty"><span className="spinner" /></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <PageHead title="Inicio" sub="No se pudo cargar el panel." />
        <div className="card" style={{ padding: 24, color: 'var(--fg-muted)' }}>
          Inténtalo de nuevo en unos segundos.
        </div>
      </div>
    );
  }

  const { todayCount, todayAgenda, pendingApprovalCount, pendingApprovalSample, pendingConfirmationCount, upcomingWeekCount, centerIds: aggregatedCenterIds } = data;
  const multiCentre = aggregatedCenterIds.length > 1;
  const subLine = [
    `${String(todayCount)} ${todayCount === 1 ? 'reserva hoy' : 'reservas hoy'}`,
    pendingApprovalCount > 0 ? `${String(pendingApprovalCount)} pendientes de aprobación` : null,
    `${String(upcomingWeekCount)} próximas en la semana`,
  ].filter(Boolean).join(' · ');

  return (
    <div>
      <PageHead
        title={greeting(now, userName)}
        sub={subLine}
        meta={
          <span className="mono" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {fmtClock(now)} · {fmtToday(now)}
          </span>
        }
      />

      {/* ─── Filtros: centro + mis reservas ─────────────────────────────── */}
      {(showCenterDropdown || linkedStaffId) && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          {showCenterDropdown && (
            <select
              className="select"
              value={activeCenterId}
              onChange={(e) => { setActiveCenterId(e.target.value); }}
              style={{ minWidth: 200 }}
            >
              {supportsAll && (
                <option value={ALL_CENTERS}>{isSuper ? 'Todos los centros' : 'Todos mis centros'}</option>
              )}
              {centerOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          {linkedStaffId && !isStaff && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={staffOnly}
                onChange={(e) => { setStaffOnly(e.target.checked); }}
              />
              Solo mis reservas
            </label>
          )}
        </div>
      )}

      {/* ─── Hero KPIs ────────────────────────────────────────────────── */}
      <div className="hero-stats">
        <button className="hero-stat" onClick={() => { void navigate('/admin/bookings'); }}>
          <div className="hero-stat-head">
            <span className="hero-stat-label">Hoy</span>
          </div>
          <div className="hero-stat-body">
            <span className="hero-stat-value mono">{todayCount}</span>
          </div>
          <div className="hero-stat-foot">
            {todayAgenda.length} confirmadas o en sala
          </div>
        </button>

        <button className="hero-stat" onClick={() => { void navigate('/admin/calendar'); }}>
          <div className="hero-stat-head">
            <span className="hero-stat-label">Próximos 7 días</span>
          </div>
          <div className="hero-stat-body">
            <span className="hero-stat-value mono">{upcomingWeekCount}</span>
          </div>
          <div className="hero-stat-foot">Reservas confirmadas</div>
        </button>

        <button
          className="hero-stat"
          data-tone={pendingApprovalCount > 0 ? 'warn' : undefined}
          onClick={() => { void navigate('/admin/bookings'); }}
        >
          <div className="hero-stat-head">
            <span className="hero-stat-label">Pend. aprobación</span>
            {pendingApprovalCount > 0 && (
              <span className="pill" data-tone="warn" style={{ fontSize: 9.5 }}>Requiere acción</span>
            )}
          </div>
          <div className="hero-stat-body">
            <span className="hero-stat-value mono">{pendingApprovalCount}</span>
          </div>
          <div className="hero-stat-foot">
            {pendingApprovalCount > 0 ? 'Solicitudes esperando tu OK' : 'Sin pendientes'}
          </div>
        </button>

        <button className="hero-stat" onClick={() => { void navigate('/admin/bookings'); }}>
          <div className="hero-stat-head">
            <span className="hero-stat-label">Sin verificar</span>
          </div>
          <div className="hero-stat-body">
            <span className="hero-stat-value mono">{pendingConfirmationCount}</span>
          </div>
          <div className="hero-stat-foot">Esperando que el cliente confirme el email</div>
        </button>
      </div>

      {/* ─── Two-column grid ──────────────────────────────────────────── */}
      <div className="inicio-grid">
        {/* Left col: approvals + agenda */}
        <div className="inicio-col">
          {pendingApprovalCount > 0 && (
            <section className="inicio-card" data-tone="warn">
              <div className="inicio-card-head">
                <div>
                  <div className="inicio-card-eyebrow">Requiere tu atención</div>
                  <h2 className="inicio-card-title">Cola de aprobaciones</h2>
                </div>
                <button className="btn" data-size="sm" onClick={() => { void navigate('/admin/bookings'); }}>
                  Ver todas <Icons.ChevR size={12} />
                </button>
              </div>
              <div className="approval-queue">
                {pendingApprovalSample.map((b) => (
                  <button key={b.id} className="approval-queue-row" onClick={() => { setOpenId(b.id); }}>
                    <div className="avatar" data-tone={tone(b.customerName)}>{initials(b.customerName)}</div>
                    <div className="approval-queue-info">
                      <div className="approval-queue-client">{b.customerName}</div>
                      <div className="approval-queue-meta">
                        <span>{b.serviceName}</span>
                        <span className="dot" />
                        <span className="mono">{fmtDateShort(b.startTime.slice(0, 10))} · {fmtTime(b.startTime)}</span>
                        {b.staffName && (<><span className="dot" /><span>{b.staffName}</span></>)}
                        {multiCentre && b.centerName && (<><span className="dot" /><span>{b.centerName}</span></>)}
                      </div>
                    </div>
                    <Icons.ChevR size={12} />
                  </button>
                ))}
                {pendingApprovalCount > pendingApprovalSample.length && (
                  <div style={{ padding: '10px 20px', borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--fg-muted)' }}>
                    Y {pendingApprovalCount - pendingApprovalSample.length} más en la lista de reservas.
                  </div>
                )}
              </div>
            </section>
          )}

          <section className="inicio-card">
            <div className="inicio-card-head">
              <div>
                <div className="inicio-card-eyebrow">Hoy · {todayAgenda.length} {todayAgenda.length === 1 ? 'cita' : 'citas'}</div>
                <h2 className="inicio-card-title">Agenda del día</h2>
              </div>
              <button className="btn" data-size="sm" onClick={() => { void navigate('/admin/calendar'); }}>
                Calendario completo <Icons.ChevR size={12} />
              </button>
            </div>
            <div className="inicio-agenda">
              {todayAgenda.length === 0 && (
                <div className="inicio-empty">Sin citas confirmadas para hoy.</div>
              )}
              {todayAgenda.map((b) => (
                <button key={b.id} className="inicio-agenda-row" onClick={() => { setOpenId(b.id); }}>
                  <span className="inicio-agenda-time mono">{fmtTime(b.startTime)}</span>
                  <span className="inicio-agenda-color" />
                  <div className="inicio-agenda-body">
                    <div className="inicio-agenda-svc">{b.serviceName}</div>
                    <div className="inicio-agenda-meta">
                      <span>{b.customerName}</span>
                      {b.staffName && (<><span className="dot" /><span>{b.staffName}</span></>)}
                      {b.roomName && (<><span className="dot" /><span>{b.roomName}</span></>)}
                      {multiCentre && b.centerName && (<><span className="dot" /><span>{b.centerName}</span></>)}
                    </div>
                  </div>
                  <span className="pill" data-tone={STATUS_MAP[b.status].tone}>{STATUS_MAP[b.status].label}</span>
                </button>
              ))}
            </div>
          </section>
        </div>

        {/* Right col: quick links — adapted por rol */}
        <div className="inicio-col">
          <section className="inicio-card">
            <div className="inicio-card-head">
              <div>
                <div className="inicio-card-eyebrow">Atajos</div>
                <h2 className="inicio-card-title">Operación rápida</h2>
              </div>
            </div>
            <Shortcuts role={role} />
          </section>
        </div>
      </div>

      {openBooking && (
        <BookingDetailDrawer booking={openBooking} onClose={() => { setOpenId(null); }} />
      )}
    </div>
  );
}
